import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";

const statusColor = {
  pending_payment: "bg-amber-100 text-amber-800",
  paid: "bg-emerald-100 text-emerald-800",
  in_production: "bg-blue-100 text-blue-800",
  shipped: "bg-purple-100 text-purple-800",
  delivered: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-rose-100 text-rose-800",
};

export default function Dashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [ol, setOl] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    api.get("/orders").then((r) => setOrders(r.data)).finally(() => setOl(false));
  }, [user, loading, nav]);

  if (loading || !user) return <div className="min-h-[50vh] flex items-center justify-center text-[color:var(--text-muted)]">Loading…</div>;

  return (
    <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="uppercase text-xs tracking-[0.3em] font-bold" style={{color: "var(--secondary)"}}>Your studio</div>
      <h1 className="font-serif text-5xl mt-3 leading-none" style={{color: "var(--primary)"}}>Welcome, {user.name?.split(" ")[0] || "friend"}.</h1>
      <p className="mt-4 text-[color:var(--text-muted)]">Track your commissions from proof to delivery.</p>

      {user.is_admin && (
        <div data-testid="admin-shortcuts" className="mt-6 flex flex-wrap gap-3">
          <a href="/admin/orders" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline bg-white text-sm card-lift" data-testid="admin-orders-link">
            <span style={{color: "var(--accent-metal)"}}>▸</span> Admin · Orders & Quotes
          </a>
          <a href="/admin/inbox" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline bg-white text-sm card-lift" data-testid="admin-inbox-link">
            <span style={{color: "var(--accent-metal)"}}>▸</span> Admin · Customer Inbox
          </a>
          <a href="/admin/discount-codes" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hairline bg-white text-sm card-lift" data-testid="admin-codes-link">
            <span style={{color: "var(--accent-metal)"}}>▸</span> Admin · Discount Codes
          </a>
        </div>
      )}

      <div className="mt-10 bg-white rounded-lg border hairline overflow-hidden">
        <div className="p-6 border-b hairline flex items-center justify-between">
          <div className="font-serif text-2xl" style={{color: "var(--primary)"}}>Orders</div>
          <div className="text-xs text-[color:var(--text-muted)]">{orders.length} total</div>
        </div>
        {ol ? (
          <div className="p-10 text-center text-[color:var(--text-muted)]">Loading…</div>
        ) : orders.length === 0 ? (
          <div data-testid="empty-orders" className="p-10 text-center text-[color:var(--text-muted)]">No orders yet. Start with the <a href="/design-studio" className="underline">Design Studio</a>.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Order</TableHead><TableHead>Material</TableHead><TableHead>Tier</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Placed</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => (
                <TableRow key={o.order_id} data-testid={`order-${o.order_id}`}>
                  <TableCell className="font-mono text-xs">{o.order_id}</TableCell>
                  <TableCell>{o.material}</TableCell>
                  <TableCell className="capitalize">
                    {o.tier}
                    {o.rush && <Badge className="ml-2 bg-amber-100 text-amber-800">Rush · 48h</Badge>}
                  </TableCell>
                  <TableCell>${(o.amount/100).toFixed(2)}</TableCell>
                  <TableCell><Badge className={statusColor[o.status] || ""}>{o.status.replace(/_/g," ")}</Badge></TableCell>
                  <TableCell className="text-xs text-[color:var(--text-muted)]">{new Date(o.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {o.type === "quote" && (
                      <a href={`/quote-received/${o.order_id}`} data-testid={`open-order-${o.order_id}`} className="text-xs underline" style={{color: "var(--accent-metal)"}}>
                        {o.status === "quoted" ? "Review quote →" : "Open"}
                      </a>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
